/**
 * System → Confidentiality Undertaking.
 *
 * The Super Admin's rollout console. Two jobs:
 *   1. The master switch — on/off, plus who it applies to, with live signed vs
 *      outstanding counts and the language breakdown. No devtools, no shell.
 *   2. The staged rollout — pick five named people, widen later. Same list,
 *      filters and bulk behaviour as System → Two-Step Sign-In, on purpose:
 *      "select all N matching" means the same thing in both places.
 *
 * Scope "selected" means only the people ticked here are gated, so switching
 * enforcement on cannot surprise the whole company.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle, Check, FileCheck2, Languages, Loader2, RefreshCw,
  ShieldCheck, ShieldOff, Users,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { useLanguage } from '../../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LANGUAGE_NAMES = {
  ar: 'العربية · Arabic', en: 'English', hi: 'हिंदी · Hindi',
  ur: 'اردو · Urdu', ne: 'नेपाली · Nepali', bn: 'বাংলা · Bengali',
  fil: 'Filipino',
};

const STATUS_FILTERS = [
  { value: 'all', label: { en: 'Everyone', ar: 'الجميع' } },
  { value: 'required', label: { en: 'Asked to sign', ar: 'مطلوب منهم التوقيع' } },
  { value: 'outstanding', label: { en: 'Asked, not signed yet', ar: 'مطلوب ولم يوقّع' } },
  { value: 'signed', label: { en: 'Signed', ar: 'وقّعوا' } },
  { value: 'off', label: { en: 'Not asked', ar: 'غير مطلوب' } },
];

const roleLabel = (role, isRTL) => {
  const map = {
    super_admin: { en: 'Super Admin', ar: 'مدير النظام' },
    it_inf: { en: 'IT INF', ar: 'تقنية المعلومات والبنية التحتية' },
    hr_manager: { en: 'HR Manager', ar: 'مدير الموارد البشرية' },
    hr_admin: { en: 'HR Admin', ar: 'إداري الموارد البشرية' },
    hr_assistant: { en: 'HR Assistant', ar: 'مساعد الموارد البشرية' },
    manager: { en: 'Manager', ar: 'مدير' },
    employee: { en: 'Employee', ar: 'موظف' },
  };
  const entry = map[role];
  return entry ? (isRTL ? entry.ar : entry.en) : role;
};

export default function ConfidentialityUndertakingPage() {
  const { isRTL } = useLanguage();

  const [policy, setPolicy] = useState(null);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ total: 0, pages: 1, roles: [] });
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);

  const [selected, setSelected] = useState([]);
  const [allMatching, setAllMatching] = useState(false);
  const [bulk, setBulk] = useState(null);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [flagged, setFlagged] = useState([]);
  const [retrying, setRetrying] = useState(false);

  const loadFlagged = useCallback(async () => {
    const { data } = await axios.get(`${API}/nda/flagged`);
    setFlagged(data.accounts || []);
    setPolicy((current) => (current ? { ...current, ...data } : current));
  }, []);

  const retryPdf = useCallback(async (userIds) => {
    setRetrying(true);
    try {
      const { data } = await axios.post(`${API}/nda/retry-pdf`, userIds
        ? { user_ids: userIds }
        : { all_flagged: true });
      setFlagged(data.accounts || []);
      setPolicy((current) => (current ? { ...current, ...data } : current));
      if (data.repaired > 0) toast.success(data.message);
      else toast.error(data.message);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error
        || (isRTL ? 'تعذّرت إعادة البناء' : 'Could not rebuild the document'));
    } finally {
      setRetrying(false);
    }
  }, [isRTL]);

  const loadPolicy = useCallback(async () => {
    const { data } = await axios.get(`${API}/nda/enforcement`);
    setPolicy(data);
  }, []);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const { data } = await axios.get(`${API}/nda/users`, {
        params: {
          page, page_size: 25,
          q: search || undefined,
          role: roleFilter === 'all' ? undefined : roleFilter,
          status: statusFilter === 'all' ? undefined : statusFilter,
        },
      });
      setUsers(data.users || []);
      setMeta({ total: data.total, pages: data.pages, roles: data.roles || [] });
    } finally {
      setUsersLoading(false);
    }
  }, [page, search, roleFilter, statusFilter]);

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([loadPolicy(), loadUsers(), loadFlagged()]);
      } catch (err) {
        toast.error(isRTL ? 'تعذّر تحميل الإعدادات' : 'Could not load the settings');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading) loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, roleFilter, statusFilter]);

  const selectionCount = allMatching ? meta.total : selected.length;

  const setEnforcement = async (next) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/nda/enforcement`, next);
      setPolicy(data);
      await loadUsers();
      toast.success(
        data.enforced
          ? (isRTL ? 'التعهد مُفعّل' : 'The undertaking is being enforced')
          : (isRTL ? 'التعهد موقوف' : 'Enforcement is off')
      );
    } catch (err) {
      toast.error(err.response?.data?.detail?.error
        || (isRTL ? 'تعذّر الحفظ' : 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  const toggleOne = async (user, required) => {
    try {
      const { data } = await axios.put(`${API}/nda/users/${user.id}/require`, {
        required,
        reason: required
          ? 'Individual rollout from the System console'
          : 'Removed from the rollout from the System console',
      });
      setPolicy((current) => ({ ...current, ...data }));
      setUsers((current) => current.map((row) => (
        row.id === user.id
          ? { ...row, required_directly: required, in_scope: required || policy?.scope === 'all' }
          : row
      )));
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail?.error
        || (isRTL ? 'تعذّر التحديث' : 'Could not update'));
    }
  };

  const openBulk = async (required) => {
    setBulk({ required });
    setReason('');
    setPreview(null);
    try {
      const { data } = await axios.post(`${API}/nda/bulk-require`, {
        required,
        reason: 'dry run preview only',
        dry_run: true,
        ...(allMatching
          ? {
            search: search || undefined,
            role: roleFilter === 'all' ? undefined : roleFilter,
            status: statusFilter === 'all' ? undefined : statusFilter,
          }
          : { user_ids: selected }),
      });
      setPreview(data);
    } catch (err) {
      setPreview(null);
    }
  };

  const applyBulk = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/nda/bulk-require`, {
        required: bulk.required,
        reason,
        ...(allMatching
          ? {
            search: search || undefined,
            role: roleFilter === 'all' ? undefined : roleFilter,
            status: statusFilter === 'all' ? undefined : statusFilter,
          }
          : { user_ids: selected }),
      });
      toast.success(data.message);
      setBulk(null);
      setSelected([]);
      setAllMatching(false);
      await Promise.all([loadPolicy(), loadUsers(), loadFlagged()]);
    } catch (err) {
      toast.error(err.response?.data?.detail?.error
        || (isRTL ? 'تعذّر التطبيق' : 'Could not apply'));
    } finally {
      setSaving(false);
    }
  };

  const languageRows = useMemo(
    () => Object.entries(policy?.by_language || {}).sort((a, b) => b[1] - a[1]),
    [policy]
  );

  if (loading) {
    return (
      <div className="flex justify-center py-24" data-testid="nda-admin-loading">
        <Loader2 className="h-6 w-6 animate-spin text-brand-orange" />
      </div>
    );
  }

  const scopeIsAll = policy?.scope === 'all';

  return (
    <div className="space-y-6" data-testid="nda-admin-page">
      {/* ── the switch ─────────────────────────────────────────────── */}
      <Card data-testid="nda-enforcement-card">
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileCheck2 className="h-5 w-5 text-brand-orange" />
                {isRTL ? 'تعهد السرية' : 'Confidentiality Undertaking'}
                <Badge
                  variant={policy?.enforced ? 'default' : 'secondary'}
                  data-testid="nda-enforcement-badge"
                >
                  {policy?.enforced
                    ? (isRTL ? 'مُفعّل' : 'Enforced')
                    : (isRTL ? 'موقوف' : 'Off')}
                </Badge>
              </CardTitle>
              <CardDescription className="mt-1">
                {isRTL
                  ? 'من يجب عليه التوقيع لا يستطيع استخدام النظام قبل الموافقة — الخيار الوحيد الموافقة أو تسجيل الخروج.'
                  : 'Anyone in scope cannot use the system until they accept — Agree or Sign out, nothing else.'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              <span className="text-sm text-muted-foreground">
                {isRTL ? 'إلزام التوقيع' : 'Require signing'}
              </span>
              <Switch
                checked={Boolean(policy?.enforced)}
                onCheckedChange={(value) => setEnforcement({
                  enforced: value,
                  reason: value ? 'Enabled from the System console'
                    : 'Disabled from the System console',
                })}
                disabled={saving}
                data-testid="nda-master-toggle"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat
              testId="nda-stat-in-scope"
              icon={Users}
              label={isRTL ? 'مطلوب منهم' : 'In scope now'}
              value={policy?.in_scope ?? 0}
            />
            <Stat
              testId="nda-stat-signed"
              icon={Check}
              label={isRTL ? 'وقّعوا' : 'Signed'}
              value={policy?.scope_signed ?? 0}
            />
            <Stat
              testId="nda-stat-outstanding"
              icon={AlertTriangle}
              label={isRTL ? 'لم يوقّعوا بعد' : 'Outstanding'}
              value={policy?.scope_outstanding ?? 0}
              tone={policy?.scope_outstanding ? 'warn' : 'plain'}
            />
            <Stat
              testId="nda-stat-selected"
              icon={ShieldCheck}
              label={isRTL ? 'محدَّدون' : 'Flagged people'}
              value={policy?.selected_accounts ?? 0}
            />
          </div>

          {/* who it applies to */}
          <div className="rounded-lg border border-brand-beige bg-brand-beige-40/40 p-4 space-y-3">
            <div className="text-sm font-medium text-brand-black">
              {isRTL ? 'على من يُطبَّق؟' : 'Who does it apply to?'}
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <ScopeChoice
                testId="nda-scope-selected"
                active={!scopeIsAll}
                title={isRTL ? 'الأشخاص المحدَّدون فقط' : 'Only the people I pick'}
                body={isRTL
                  ? `حالياً ${policy?.selected_accounts ?? 0} شخص. ابدأ بخمسة ثم وسّع.`
                  : `${policy?.selected_accounts ?? 0} flagged today. Start with five, widen later.`}
                onClick={() => setEnforcement({
                  enforced: Boolean(policy?.enforced), scope: 'selected',
                  reason: 'Scope narrowed to the flagged accounts',
                })}
                disabled={saving}
              />
              <ScopeChoice
                testId="nda-scope-all"
                active={scopeIsAll}
                title={isRTL ? 'كل الحسابات' : 'Every account'}
                body={isRTL
                  ? `${policy?.active_accounts ?? 0} حساب نشط سيُطلب منهم التوقيع.`
                  : `All ${policy?.active_accounts ?? 0} active accounts would be asked.`}
                onClick={() => setEnforcement({
                  enforced: Boolean(policy?.enforced), scope: 'all',
                  reason: 'Scope widened to every account',
                })}
                disabled={saving}
                danger
              />
            </div>
          </div>

          {languageRows.length > 0 && (
            <div data-testid="nda-language-breakdown">
              <div className="text-sm font-medium text-brand-black flex items-center gap-2 mb-2">
                <Languages className="h-4 w-4 text-brand-brown" />
                {isRTL ? 'لغات التوقيع' : 'Signed in which language'}
              </div>
              <div className="flex flex-wrap gap-2">
                {languageRows.map(([code, count]) => (
                  <Badge
                    key={code}
                    variant="outline"
                    className="font-normal"
                    data-testid={`nda-language-count-${code}`}
                  >
                    {LANGUAGE_NAMES[code] || code}
                    <span className="ms-2 font-medium">{count}</span>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {Boolean(policy?.flagged) && (
            <div
              className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
              data-testid="nda-pdf-failures"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                {isRTL
                  ? `${policy.flagged} توقيع بدون ملف PDF. التوقيع صحيح والدخول غير محجوب، لكن لا يوجد مستند محفوظ — راجع القائمة أدناه.`
                  : `${policy.flagged} signature(s) have no filed document. The signature stands and nobody is blocked, but there is nothing on file — see the list below.`}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── signed, but nothing on file ─────────────────────────────── */}
      {flagged.length > 0 && (
        <Card className="border-amber-300" data-testid="nda-flagged-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-amber-900">
              <AlertTriangle className="h-5 w-5" />
              {isRTL ? 'وقّعوا بدون مستند محفوظ' : 'Signed, but no document on file'}
              <span className="text-sm font-normal text-muted-foreground">
                {flagged.length}
              </span>
            </CardTitle>
            <CardDescription>
              {isRTL
                ? 'التوقيع مُسجَّل ولا أحد محجوب. يُعاد بناء الملف تلقائياً عند تسجيل الدخول القادم، أو أعد المحاولة الآن.'
                : 'Their acceptance is recorded and nobody is blocked. The file is rebuilt automatically the next time they sign in — or retry now.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {flagged.map((account) => (
              <div
                key={account.id}
                className="flex flex-wrap items-start justify-between gap-3 rounded-lg border bg-white px-3 py-2 text-sm"
                data-testid={`nda-flagged-row-${account.id}`}
              >
                <div className="space-y-1">
                  <div className="font-medium">
                    {account.name}
                    <span className="ms-2 text-xs text-muted-foreground">
                      {account.email || (isRTL ? 'بدون بريد' : 'no email')}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {account.pdf_status === 'no_employee_record'
                      ? (isRTL
                        ? 'لا يوجد ملف موظف مرتبط بهذا الحساب، فلا مكان لحفظ المستند.'
                        : 'No employee record is linked to this account, so there is nowhere to file it.')
                      : (account.last_error || (isRTL ? 'سبب غير معروف' : 'reason unknown'))}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {isRTL ? 'محاولات: ' : 'Attempts: '}{account.attempts}
                    {account.accepted_at && (
                      <span dir="ltr">
                        {' · '}
                        {isRTL ? 'وقّع في ' : 'signed '}
                        {new Date(account.accepted_at)
                          .toLocaleString(isRTL ? 'ar-SA' : 'en-GB')}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={retrying}
                  onClick={() => retryPdf([account.id])}
                  data-testid={`nda-retry-${account.id}`}
                >
                  {retrying
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <RefreshCw className="h-4 w-4 me-1" />}
                  {isRTL ? 'أعد المحاولة' : 'Retry'}
                </Button>
              </div>
            ))}
            <Button
              size="sm"
              disabled={retrying}
              onClick={() => retryPdf(null)}
              data-testid="nda-retry-all"
            >
              {retrying && <Loader2 className="h-4 w-4 animate-spin me-2" />}
              {isRTL ? 'أعد المحاولة للجميع' : 'Retry all'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── the people ─────────────────────────────────────────────── */}
      <Card data-testid="nda-user-list">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            {isRTL ? 'حسب الشخص' : 'By person'}
            <span className="text-sm font-normal text-muted-foreground">
              {meta.total} {isRTL ? 'حساب' : 'accounts'}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { loadPolicy(); loadUsers(); loadFlagged(); }}
              data-testid="nda-refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </CardTitle>
          <CardDescription>
            {isRTL
              ? 'اللغة تُحدَّد من الجنسية في ملف الموظف؛ وإن لم تُعرف فالإنجليزية.'
              : 'Language comes from the nationality on the employee record; English when it is not known.'}
          </CardDescription>

          <div className="flex flex-wrap items-center gap-2 pt-3">
            <Input
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1); }}
              placeholder={isRTL ? 'ابحث بالاسم أو البريد' : 'Search by name or email'}
              className="h-9 w-full sm:w-64"
              data-testid="nda-user-search"
            />
            <select
              value={roleFilter}
              onChange={(event) => { setRoleFilter(event.target.value); setPage(1); }}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="nda-role-filter"
            >
              <option value="all">{isRTL ? 'كل الأدوار' : 'All roles'}</option>
              {(meta.roles || []).map((role) => (
                <option key={role} value={role}>{roleLabel(role, isRTL)}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(event) => { setStatusFilter(event.target.value); setPage(1); }}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="nda-status-filter"
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {isRTL ? option.label.ar : option.label.en}
                </option>
              ))}
            </select>
            {usersLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>

          {selectionCount > 0 && (
            <div
              className="flex flex-wrap items-center justify-between gap-3 mt-3 p-3 rounded-lg border border-brand-orange/40 bg-brand-orange/5"
              data-testid="nda-bulk-bar"
            >
              <div className="text-sm">
                <span className="font-medium" data-testid="nda-selection-count">
                  {selectionCount} {isRTL ? 'محدَّد' : 'selected'}
                </span>
                {!allMatching && meta.total > users.length && (
                  <button
                    type="button"
                    onClick={() => setAllMatching(true)}
                    className="ms-2 text-brand-brown hover:text-brand-orange underline"
                    data-testid="nda-select-all-matching"
                  >
                    {isRTL
                      ? `تحديد كل ${meta.total} المطابقين`
                      : `Select all ${meta.total} matching`}
                  </button>
                )}
                {allMatching && (
                  <button
                    type="button"
                    onClick={() => { setAllMatching(false); setSelected([]); }}
                    className="ms-2 text-muted-foreground hover:text-brand-black underline"
                    data-testid="nda-clear-selection"
                  >
                    {isRTL ? 'إلغاء التحديد' : 'Clear selection'}
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => openBulk(true)} data-testid="nda-bulk-require">
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  {isRTL ? 'إلزام المحدَّدين' : 'Ask selected to sign'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openBulk(false)}
                  data-testid="nda-bulk-unrequire"
                >
                  <ShieldOff className="h-4 w-4 mr-2" />
                  {isRTL ? 'إلغاء الإلزام' : 'Stop asking'}
                </Button>
              </div>
            </div>
          )}
        </CardHeader>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-brand-beige-40/60 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-3 w-10">
                    <Checkbox
                      checked={users.length > 0 && selected.length === users.length}
                      onCheckedChange={(value) => {
                        setAllMatching(false);
                        setSelected(value ? users.map((u) => u.id) : []);
                      }}
                      data-testid="nda-select-page"
                    />
                  </th>
                  <th className="p-3 text-start">{isRTL ? 'الشخص' : 'Person'}</th>
                  <th className="p-3 text-start">{isRTL ? 'الدور' : 'Role'}</th>
                  <th className="p-3 text-start">{isRTL ? 'اللغة' : 'Language'}</th>
                  <th className="p-3 text-start">{isRTL ? 'الحالة' : 'Status'}</th>
                  <th className="p-3 text-end">{isRTL ? 'مطلوب؟' : 'Asked to sign'}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="border-t border-brand-beige-60 hover:bg-brand-beige-40/30"
                    data-testid={`nda-user-row-${user.id}`}
                  >
                    <td className="p-3">
                      <Checkbox
                        checked={allMatching || selected.includes(user.id)}
                        disabled={allMatching}
                        onCheckedChange={(value) => setSelected((current) => (
                          value
                            ? [...current, user.id]
                            : current.filter((id) => id !== user.id)
                        ))}
                        data-testid={`nda-select-${user.id}`}
                      />
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-brand-black">{user.name}</div>
                      <div className="text-xs text-muted-foreground">{user.email}</div>
                      {user.employee_number && (
                        <div className="text-xs text-muted-foreground">
                          #{user.employee_number}
                        </div>
                      )}
                    </td>
                    <td className="p-3">{roleLabel(user.role, isRTL)}</td>
                    <td className="p-3">
                      <div>{LANGUAGE_NAMES[user.language] || user.language}</div>
                      {!user.language_from_nationality && (
                        <div className="text-xs text-muted-foreground">
                          {isRTL ? 'لا جنسية مسجّلة' : 'no nationality on file'}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      {user.signed ? (
                        <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                          <Check className="h-3 w-3 mr-1" />
                          {isRTL ? 'وقّع' : 'Signed'}
                        </Badge>
                      ) : user.in_scope ? (
                        <Badge variant="outline" className="border-amber-300 text-amber-800">
                          {isRTL ? 'في الانتظار' : 'Waiting'}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">
                          {isRTL ? 'غير مطلوب' : 'Not asked'}
                        </span>
                      )}
                      {!user.has_employee_record && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {isRTL ? 'لا ملف موظف — بلا PDF' : 'no employee record — no PDF'}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-end">
                      <Switch
                        checked={Boolean(user.required_directly)}
                        onCheckedChange={(value) => toggleOne(user, value)}
                        data-testid={`nda-toggle-${user.id}`}
                      />
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-muted-foreground">
                      {isRTL ? 'لا نتائج' : 'Nothing matches those filters'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between p-4 border-t border-brand-beige-60">
            <span className="text-sm text-muted-foreground" data-testid="nda-page-label">
              {isRTL ? `صفحة ${page} من ${meta.pages}` : `Page ${page} of ${meta.pages}`}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || usersLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="nda-prev-page"
              >
                {isRTL ? 'السابق' : 'Previous'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= meta.pages || usersLoading}
                onClick={() => setPage((p) => p + 1)}
                data-testid="nda-next-page"
              >
                {isRTL ? 'التالي' : 'Next'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── confirm a bulk change ──────────────────────────────────── */}
      <Dialog open={Boolean(bulk)} onOpenChange={(open) => !open && setBulk(null)}>
        <DialogContent data-testid="nda-bulk-dialog">
          <DialogHeader>
            <DialogTitle>
              {bulk?.required
                ? (isRTL ? 'إلزام التوقيع' : 'Ask these people to sign')
                : (isRTL ? 'إلغاء الإلزام' : 'Stop asking these people')}
            </DialogTitle>
            <DialogDescription>
              {preview ? (
                <span data-testid="nda-bulk-preview">
                  {isRTL
                    ? `${preview.matched} حساب · ${preview.already_signed} وقّعوا مسبقاً · ${preview.without_employee_record} بلا ملف موظف`
                    : `${preview.matched} account(s) · ${preview.already_signed} already signed · ${preview.without_employee_record} with no employee record`}
                </span>
              ) : (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              {isRTL ? 'السبب (10 أحرف على الأقل)' : 'Reason (10 characters minimum)'}
            </label>
            <Textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              placeholder={isRTL
                ? 'مثال: المرحلة الأولى — خمسة أشخاص من الإنتاج'
                : 'e.g. Phase one — five people from Production'}
              data-testid="nda-bulk-reason"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulk(null)} data-testid="nda-bulk-cancel">
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={applyBulk}
              disabled={reason.trim().length < 10 || saving}
              data-testid="nda-bulk-confirm"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isRTL ? 'تطبيق' : 'Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, tone = 'plain', testId }) => (
  <div
    className={`rounded-lg border p-3 ${
      tone === 'warn'
        ? 'border-amber-300 bg-amber-50'
        : 'border-brand-beige bg-white'
    }`}
    data-testid={testId}
  >
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Icon className="h-3.5 w-3.5" />
      {label}
    </div>
    <div className="text-2xl font-semibold text-brand-black mt-1">{value}</div>
  </div>
);

const ScopeChoice = ({ active, title, body, onClick, disabled, danger, testId }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`text-start rounded-lg border p-3 transition-colors ${
      active
        ? (danger ? 'border-amber-400 bg-amber-50' : 'border-brand-orange bg-brand-orange/5')
        : 'border-brand-beige bg-white hover:border-brand-orange/50'
    } disabled:opacity-60`}
    data-testid={testId}
  >
    <div className="flex items-center gap-2 text-sm font-medium text-brand-black">
      {active && <Check className="h-4 w-4 text-brand-orange" />}
      {title}
    </div>
    <div className="text-xs text-muted-foreground mt-1">{body}</div>
  </button>
);
